import * as React from 'react';
import { useEffect, useState } from 'react';

import { useFeatureValue } from '@growthbook/growthbook-react';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import accountIdsToBeBlacklisted from '@gumtree/shared/src/partnerships/motors-finance-blacklist';
import { getLinkHostname } from '@gumtree/shared/src/util/ga4-bff';
import { Icon, Link } from '@gumtree/ui-library';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';
import {
    removeTrailingZeros,
    setPriceToZeroIfFreebies,
} from '@gumtree/ui-library/src/utils/currency-service';

import { VipState } from '../../reducers';
import {
    HeaderBlockContainer,
    VipAddress,
    VipTitle,
    VipPrice,
    HeaderBlockInfo,
    HeaderAddressBlock,
    VatInfo,
    CarMoneyMonthlyPrice,
    HeaderPriceBlock,
} from './index.style';
import SellerBuyBlock from '../seller-buy-block';

export const smoothScrollMap = () => {
    const scrollToEl = document.querySelector('.map-container-scroll');
    scrollToEl?.scrollIntoView({ behavior: 'smooth' });
};

const HeaderBlock: React.FC<Props> = ({
    title,
    address,
    vatInfo,
    price,
    isMobileCardsLayout = false,
    visibleOnMap = true,
    isSellerCinchCar,
    l2CategoryId,
    hiddeAddress = false,
    isPurchaseAvailable,
    monthlyPrice,
    compactUrl,
    sellerType,
    setIsBuyerProtectionOpen,
}) => {
    const [isWhitelistedAccountId, setIsWhitelistedAccountId] = useState(false);
    const renderedPrice = removeTrailingZeros(setPriceToZeroIfFreebies(price, l2CategoryId));
    const showCarMoneyMonthlyPrice =
        useFeatureValue(GrowthBookFeature.CARMONEY_MONTHLY_PRICE, 'A') === 'B' &&
        isWhitelistedAccountId;

    useEffect(() => {
        const accountId = window.clientData?.partnerships?.accountId || null;
        setIsWhitelistedAccountId(!accountIdsToBeBlacklisted.includes(parseInt(accountId)));
    }, []);

    return (
        <HeaderBlockContainer>
            <VipTitle {...qaAttribute('vip-title')}>{title}</VipTitle>
            <HeaderBlockInfo
                isMobileCardsLayout={isMobileCardsLayout}
                showCarMoneyMonthlyPrice={showCarMoneyMonthlyPrice}
            >
                <HeaderAddressBlock
                    isMobileCardsLayout={isMobileCardsLayout}
                    {...(isMobileCardsLayout && visibleOnMap && { onClick: smoothScrollMap })}
                >
                    {isMobileCardsLayout && !isSellerCinchCar && <Icon type="beacon" />}
                    {!hiddeAddress && (
                        <VipAddress
                            data-q="ad-location"
                            isSellerCinchCar={isSellerCinchCar}
                            isMobileCardsLayout={isMobileCardsLayout}
                        >
                            {address}
                        </VipAddress>
                    )}
                </HeaderAddressBlock>
                <HeaderPriceBlock showCarMoneyMonthlyPrice={showCarMoneyMonthlyPrice}>
                    {renderedPrice && !isPurchaseAvailable ? (
                        <>
                            <VipPrice data-q="ad-price" isMobileCardsLayout={isMobileCardsLayout}>
                                {renderedPrice}
                            </VipPrice>
                            {vatInfo && <VatInfo data-q="ad-price-vat">{`(${vatInfo})`}</VatInfo>}
                        </>
                    ) : null}
                </HeaderPriceBlock>
                {showCarMoneyMonthlyPrice &&
                    isMobileCardsLayout &&
                    monthlyPrice &&
                    compactUrl &&
                    sellerType === 'Trade' && (
                        <CarMoneyMonthlyPrice>
                            £{monthlyPrice} per month
                            <Link
                                href={compactUrl}
                                openInNewTab
                                onClick={() => {
                                    trackGA4Event<GA4.ClickPartnerAdEvent>({
                                        event: 'click_on_partner_ad',
                                        linkDomain: getLinkHostname(compactUrl),
                                        linkURL: compactUrl,
                                        linkText: 'Apply now',
                                        advertisingBannerName: 'carmoney',
                                        advertisingBannerType: 'Compact',
                                        adIsTrade: Boolean(sellerType === 'Trade'),
                                    });
                                }}
                            >
                                Apply now
                            </Link>
                        </CarMoneyMonthlyPrice>
                    )}
            </HeaderBlockInfo>
            {isPurchaseAvailable && isMobileCardsLayout && (
                <SellerBuyBlock
                    isMobileCardsLayout={isMobileCardsLayout}
                    setIsBuyerProtectionOpen={setIsBuyerProtectionOpen}
                />
            )}
        </HeaderBlockContainer>
    );
};

export type Props = Pick<VipState['adDetails'], 'title' | 'address' | 'price' | 'l2CategoryId'> & {
    isMobileCardsLayout?: boolean;
    visibleOnMap?: boolean;
    vatInfo?: string;
    isSellerCinchCar: boolean;
    hiddeAddress?: boolean;
    isPurchaseAvailable?: boolean;
    showBuyBlock?: boolean;
    monthlyPrice?: number;
    compactUrl?: string;
    sellerType?: string;
    setIsBuyerProtectionOpen: (isOpen: boolean) => void;
};

HeaderBlock.defaultProps = {
    isMobileCardsLayout: false,
    visibleOnMap: true,
    vatInfo: '',
};

export default HeaderBlock;
